import { useCallback, useEffect, useState } from 'react';

export type SelectionClickModifiers = {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

export const useTimelineSelection = () => {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(() => new Set());

  const clearSelection = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const replaceSelection = useCallback((ids: Iterable<number>) => {
    setSelectedIds(new Set(ids));
  }, []);

  const toggleInSelection = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleItemSelect = useCallback(
    (id: number, modifiers: SelectionClickModifiers): boolean => {
      if (modifiers.shiftKey || modifiers.metaKey || modifiers.ctrlKey) {
        toggleInSelection(id);
        return true;
      }
      return false;
    },
    [toggleInSelection],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  return {
    selectedIds,
    clearSelection,
    replaceSelection,
    handleItemSelect,
  };
};
